import Vue from "vue";
import axios from "axios";

export function hasNewPosts(channel) {
  if (!channel.latest_post) {
    return false;
  }
  return channel.last_viewed
    ? channel.latest_post.created > channel.last_viewed
    : true;
}

export default {
  state: {
    initialSearchCache: null,
    searchResults: [...Array(20).keys()],
    postCache: {},
    searching: false,
    loading: false,
    total: 0,
    searchParams: {},
    initialResults: null,
    channels: [],
    publicChannelCount: 0,
    createPost: false,
    channelId: null,
    activePostId: false,
  },
  mutations: {
    updatePostProps(state, post) {
      Vue.set(state.postCache, post.id, post);
    },
    updatePostCache(state, posts) {
      state.loading = false;
      posts.forEach((post) => Vue.set(state.postCache, post.id, post));
    },
    setPostSearchResults(state, { results, total }) {
      if (state.searchParams.skip === 0) {
        state.searchResults = results;

        // If there are no filters (i.e. default all post view) cache a copy
        let p = state.searchParams;
        if (
          (!p.category || p.category === "all") &&
          !p.search &&
          !p.tags &&
          !p.channel
        ) {
          state.initialSearchCache = [...results];
        }
      } else {
        state.searchResults.push(...results);
      }
      state.total = total;
      state.searching = false;
    },
    setPostsLoading(state, loading) {
      state.loading = loading;
    },
    setPostSearchParams(state, params) {
      state.searchParams = params;
      state.searching = true;
    },
    clearPostSearchResults(state) {
      state.searchResults = [...Array(20).keys()];
    },
    updateChannels(state, { channels, public_channel_count }) {
      function orderByNewPosts(channels) {
        return channels.sort((x, y) => {
          return hasNewPosts(x) == hasNewPosts(y) ? 0 : hasNewPosts(x) ? -1 : 1;
        });
      }
      state.publicChannelCount = public_channel_count;
      state.channels = orderByNewPosts(channels);
    },
    prependPost(state, postId) {
      state.searchResults.unshift(postId);
    },
    removePostFromResults(state, postId) {
      let index = state.searchResults.indexOf(postId);
      if (index > -1) {
        state.searchResults.splice(index, 1);
      }
    },
    useCachedSearch(state) {
      if (state.initialSearchCache !== null) {
        state.searchResults = [...state.initialSearchCache];
      }
    },
    setCreatePost(state) {
      state.createPost = true;
    },
    clearDrawer(state) {
      state.activePostId = null;
      state.createPost = false;
      state.postTemplate = null;
      // Remove any lingering Redactor link editing modals
      let modals = document.getElementsByClassName("redactor-modal-box");
      while (modals.length > 0) {
        modals[0].parentNode.removeChild(modals[0]);
      }
    },
    setChannelId(state, channelId) {
      state.channelId = channelId;
    },
    setActivePostId(state, postId) {
      state.activePostId = postId;
    },
    markVideoReady(state, { card, image }) {
      let mark = (video) => {
        if (
          video &&
          video.card_type === card.card_type &&
          video.id === card.id
        ) {
          Vue.set(video, "ready", true);
          Vue.set(video, "image", image);
        }
      };

      Object.keys(state.postCache).forEach((postId) => {
        let post = state.postCache[postId];
        mark(post.card);
        if (post.videos) {
          post.videos.forEach((video) => mark(video));
        }
      });
    },
  },
  getters: {
    channelId(state) {
      return state.channelId;
    },
    activePostId(state) {
      return state.activePostId;
    },
    postCache(state) {
      return state.postCache;
    },
    communityLoading(state) {
      return (
        state.searchResults.length === 0 && (state.loading || state.searching)
      );
    },
    posts(state) {
      return state.searchResults.map((postId) => {
        let post = state.postCache[postId];
        if (post === undefined) {
          post = { id: postId, loading: true };
        }
        return post;
      });
    },
    morePostsAvailable(state) {
      // true if we have fewer results locally than the server indicated are available
      return (
        state.searchResults.length &&
        state.total - state.searchResults.length > 0
      );
    },
    canLoadMorePosts(state, getters) {
      // true if there are more posts available, and we are not already searching for more.
      // used to control whether to allow requesting more.
      return getters.morePostsAvailable && !state.searching;
    },
    channels(state) {
      return state.channels;
    },
    publicChannelCount(state) {
      return state.publicChannelCount;
    },
    createPost(state) {
      return state.createPost;
    },
    activeChannel(state) {
      if (state.channelId) {
        return state.channels.find(channel => channel.id === state.channelId);
      }
    }
  },
  actions: {
    async searchPosts(
      { commit, dispatch },
      { category, tags, search, channel }
    ) {
      let params = { skip: 0 };
      let defaultSearch = true;
      if (category) {
        params.category = category;
        if (category !== "all") {
          defaultSearch = false;
        }
      }
      if (tags && tags.length) {
        params.tags = tags;
        defaultSearch = false;
      }
      if (search) {
        params.search = search;
        defaultSearch = false;
      }
      if (channel) {
        params.channel = channel;
        defaultSearch = false;
      }
      commit("setPostSearchParams", params);

      // If there are no search params (i.e. initial load), try to use cached version
      if (defaultSearch) {
        commit("useCachedSearch");
      }
      dispatch("fetchSearchResults");
    },
    async loadMorePosts({ commit, dispatch, getters, state }) {
      if (getters.canLoadMorePosts) {
        let params = state.searchParams;
        params.skip = state.searchResults.length;
        commit("setPostSearchParams", params);
        dispatch("fetchSearchResults");
      }
    },
    async fetchSearchResults({ commit, dispatch, state }) {
      let response = await axios.get("/api/community/search", {
        params: state.searchParams,
      });

      commit("setPostSearchResults", response.data);

      if (response.data.results.length) {
        dispatch("fetchPosts", response.data.results);
      }
    },
    async fetchPosts({ commit, state }, postIds) {
      let missingIds = postIds.filter(
        (id) => !state.postCache.hasOwnProperty(id)
      );
      if (missingIds.length) {
        commit("setPostsLoading", true);
        let params = { ids: missingIds.join(",") };
        try {
          let res = await axios.get("/api/community/posts", {
            params,
          });
          commit("updatePostCache", res.data.results);
        } catch (e) {
          console.error(e);
          commit("setPostsLoading", false);
        }
      }
    },
    async fetchChannels({ commit }) {
      let res;
      try {
        res = await axios.get("/community/api/channels");
        commit("updateChannels", res.data);
      } catch (e) {
        console.error("Unable to fetch channels: ", e);
      }
    },
    showNewPost({ commit, dispatch }, postId) {
      commit("prependPost", postId);
      dispatch("fetchPosts", [postId]);
    },
  },
};
